import { useEffect, useState, useCallback } from "react";
import { formatCurrency } from "@/utils";
import { Flex, Heading, HStack, Icon, Text, VStack } from "@chakra-ui/react";
import { FiAlertTriangle, FiRefreshCw } from "react-icons/fi";
import { useCurrentBalance } from "@/hooks/useCurrentBalance";
import { usePaymentRefresh } from "@/contexts/PaymentRefreshContext";
import { Button, Loading } from "barte-design-system";
const DEFAULT_TIMEOUT = 15000;
const DEFAULT_RETRY_ATTEMPTS = 2;

const BalanceCard = ({
    autoRefreshInterval = 300000,
    requestTimeout = DEFAULT_TIMEOUT,
    showRefreshButton = true
} = {}) => {
    const [state, setState] = useState({
        showExtrato: false,
        lastRefreshTime: null,
        errorCount: 0,
        isManualRefresh: false
    });

    const {
        currentBalance,
        loading,
        error,
        reload,
        lastUpdated
    } = useCurrentBalance(autoRefreshInterval, requestTimeout);

    const { refreshTrigger } = usePaymentRefresh();

    const handleRefreshTrigger = useCallback(() => {
        if (refreshTrigger > 0) {
            setState(prev => ({ ...prev, isManualRefresh: false }));
            reload();
        }
    }, [refreshTrigger, reload]);

    useEffect(() => {
        handleRefreshTrigger();
    }, [handleRefreshTrigger]);
    const formatLastUpdated = useCallback((dateString) => {
        if (!dateString) return 'Nunca';

        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return 'Data inválida';
            }
            return date.toLocaleDateString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            console.error('[BALANCE_CARD] Erro ao formatar data:', error);
            return 'Data inválida';
        }
    }, []);

    const handleManualRefresh = useCallback(async () => {
        try {
            setState(prev => ({
                ...prev,
                isManualRefresh: true,
                lastRefreshTime: new Date(),
                errorCount: 0
            }));
            await reload();
        } catch (error) {
            console.error('[BALANCE_CARD] Erro no refresh manual:', error);
            setState(prev => ({
                ...prev,
                errorCount: prev.errorCount + 1
            }));
        }
    }, [reload]);

    const handleExtractModal = useCallback((show) => {
        setState(prev => ({ ...prev, showExtrato: show }));
    }, []);

    const getErrorMessage = useCallback((error) => {
        if (!error) return null;

        if (error.includes('timeout')) {
            return 'Timeout na requisição. Tente novamente.';
        }
        if (error.includes('network') || error.includes('conectividade')) {
            return 'Erro de conexão. Verifique sua internet.';
        }
        if (error.includes('401') || error.includes('token')) {
            return 'Sessão expirada. Faça login novamente.';
        }

        return 'Erro ao carregar saldo. Tente novamente.';
    }, []);

    const shouldShowRetryButton = useCallback(() => {
        return error && state.errorCount < DEFAULT_RETRY_ATTEMPTS;
    }, [error, state.errorCount]);

    return (
        <Flex
            borderRadius="8px"
            width="45%"
            padding='16px 24px 16px 24px'
            bgColor="white"
            boxShadow="md"
        >
            <Flex
                w="100%"
                justify="space-between"
                gap="4"
                align="center"
            >
                <Flex
                    flexDir="column"
                    gap="2"
                    w="100%"
                >
                    <Flex
                        align="center"
                        gap="4"
                    >
                        <Heading fontSize="1rem" fontWeight={400} color="gray.900">
                            Saldo disponível
                        </Heading>
                        {loading && (
                            <Loading type="icon" />
                        )}
                    </Flex>
                    {error ? (
                        <VStack spacing="2" align="start">
                            <HStack spacing="2">
                                <Icon as={FiAlertTriangle} color="red.500" boxSize="4" />
                                <Text fontSize="sm" color="red.500">
                                    {getErrorMessage(error)}
                                </Text>
                            </HStack>
                            {shouldShowRetryButton() && (
                                <Button
                                    variant="link"
                                    size="sm"
                                    onClick={handleManualRefresh}
                                 
                                >
                                    {loading && state.isManualRefresh ? 'Tentando...' : 'Tentar novamente'}
                              
                                </Button>
                            )}
                            {state.lastRefreshTime && (
                                <Text fontSize="xs" color="gray.400">
                                    Última tentativa: {formatLastUpdated(state.lastRefreshTime.toISOString())}
                                </Text>
                            )}
                        </VStack>
                    ) : (
                        <VStack spacing="1" align="start">
                            <Flex align="baseline" gap="1">
                                <Text fontSize="1rem" fontWeight={400} color="gray.600">
                                    R$
                                </Text>
                                <Text fontSize="2.4rem" fontWeight={600} color="gray.600">
                                    {loading ? '...' : formatCurrency({ value: currentBalance }).replace('R$', '').trim()}
                                </Text>
                            </Flex>
                            <Text fontSize="0.8rem" color="gray.500">
                                Atualizado em {loading ? '...' : formatLastUpdated(lastUpdated)}
                            </Text>
                        </VStack>
                    )}
                </Flex>
                <Flex
                    gap="2"
                    align="center"
                >
                   
                    {showRefreshButton && (
                        <Button
                            variant="link"
                            size="sm"
                            onClick={handleManualRefresh}
                            disabled={loading}
                      
                        >
                            <Icon as={FiRefreshCw} boxSize="4" className={loading ? 'animate-spin' : ''} />
                        </Button>
                    )}
                </Flex>
            </Flex>
        </Flex>
    );
};

export default BalanceCard;